import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Gift, Heart, Lock, ExternalLink, Sparkles } from "lucide-react";
import Footer from "../components/Footer";

// Priority labels matching the real app
const PRIORITY_LABELS = {
  1: { label: "Un petit kiff", emoji: "😊", color: "bg-muted text-muted-foreground" },
  2: { label: "Ça me ferait plaisir", emoji: "😄", color: "bg-accent text-accent-foreground" },
  3: { label: "J'en rêve", emoji: "🔥", color: "bg-primary text-primary-foreground" }
};

// Demo wishlist items
const DEMO_ITEMS = [
  {
    id: "demo_1",
    name: "Air Jordan 1 Retro High OG",
    price: "180€",
    priority: 3,
    image: "https://images.unsplash.com/photo-1669205073423-5da5a5280572?w=400&h=300&fit=crop",
    note: "Pointure 38, coloris Chicago de préférence !",
    category: "Chaussures",
    preReserved: false
  },
  {
    id: "demo_2",
    name: "Kindle Paperwhite",
    price: "159€",
    priority: 2,
    image: "https://images.unsplash.com/photo-1646342622814-2bec9a4b521b?w=400&h=300&fit=crop",
    note: "Le modèle avec 16Go",
    category: "Électronique",
    preReserved: true // Already reserved by someone else
  },
  {
    id: "demo_3",
    name: "Cours de poterie - 5 séances",
    price: "120€",
    priority: 3,
    image: "https://images.pexels.com/photos/16303094/pexels-photo-16303094.jpeg?w=400&h=300&fit=crop",
    note: null,
    category: "Expériences",
    preReserved: false
  },
  {
    id: "demo_4",
    name: "Bougie Diptyque Baies",
    price: "45€",
    priority: 1,
    image: "https://images.unsplash.com/photo-1608263153703-caa6b0fd7bc7?w=400&h=300&fit=crop",
    note: "Le grand modèle 190g",
    category: "Décoration",
    preReserved: false
  },
  {
    id: "demo_5",
    name: "Abonnement Spotify Premium 1 an",
    price: "99€",
    priority: 2,
    image: "https://images.pexels.com/photos/5077404/pexels-photo-5077404.jpeg?w=400&h=300&fit=crop",
    note: null,
    category: "Abonnements",
    preReserved: false
  },
  {
    id: "demo_6",
    name: "Roman \"Intermezzo\" de Sally Rooney",
    price: "22€",
    priority: 1,
    image: "https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?w=400&h=300&fit=crop",
    note: "Version française",
    category: "Livres",
    preReserved: false
  },
  {
    id: "demo_7",
    name: "Sac à dos Herschel Little America",
    price: "95€",
    priority: 2,
    image: "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=300&fit=crop",
    note: "Coloris noir ou navy",
    category: "Accessoires",
    preReserved: false
  }
];

// Calculate days until birthday (12 days from now)
const calculateCountdown = () => {
  const now = new Date();
  const birthday = new Date(now);
  birthday.setDate(birthday.getDate() + 12);
  return 12;
};

export default function Demo() {
  const [items, setItems] = useState(DEMO_ITEMS);
  const [filteredItems, setFilteredItems] = useState(DEMO_ITEMS);
  const [activeFilter, setActiveFilter] = useState(null);
  const [reservedItems, setReservedItems] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [justReservedItem, setJustReservedItem] = useState(null);
  const [countdown] = useState(calculateCountdown());

  // Load reserved items from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("giftme_demo_reserved");
    if (saved) {
      setReservedItems(JSON.parse(saved));
    }
  }, []);

  // Filter items
  const filterItems = (priority) => {
    if (priority === activeFilter) {
      setFilteredItems(items);
      setActiveFilter(null);
    } else {
      setFilteredItems(items.filter(item => item.priority === priority));
      setActiveFilter(priority);
    }
  };

  // Handle reservation
  const handleReserve = (item) => {
    const newReserved = { ...reservedItems, [item.id]: true };
    setReservedItems(newReserved);
    localStorage.setItem("giftme_demo_reserved", JSON.stringify(newReserved));
    setJustReservedItem(item);
    setShowModal(true);
  };

  // Check if item is reserved
  const isReservedByMe = (itemId) => reservedItems[itemId] === true;
  const isReservedByOther = (item) => item.preReserved && !isReservedByMe(item.id);

  // Render button based on reservation status
  const renderDemoButton = (item, reservedByMe, reservedByOther) => {
    if (reservedByMe) {
      return (
        <Button
          disabled
          variant="outline"
          className="w-full rounded-full font-bold text-green-600 border-green-200"
        >
          ✅ Tu offres ce cadeau !
        </Button>
      );
    }
    
    if (reservedByOther) {
      return (
        <Button
          disabled
          className="w-full rounded-full font-bold"
        >
          🔒 Quelqu'un s'en occupe déjà
        </Button>
      );
    }
    
    return (
      <Button
        data-testid={`reserve-btn-${item.id}`}
        onClick={() => handleReserve(item)}
        className="w-full rounded-full font-bold bg-primary hover:bg-primary/90"
      >
        <Heart className="w-4 h-4 mr-2" />
        🤫 Je l'offre !
      </Button>
    );
  };

  return (
    <div className="min-h-screen pb-12 flex flex-col bg-background">
      {/* Demo Banner */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-b border-primary/20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-center gap-2 text-center">
          <span className="flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-muted-foreground">Ceci est une liste de démonstration</span>
          </span>
          <Link to="/register" className="text-primary hover:underline font-semibold text-sm">
            Crée ta propre liste gratuitement →
          </Link>
        </div>
      </div>

      {/* Header */}
      <header className="bg-card/80 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Gift className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="font-heading text-xl font-bold" data-testid="demo-owner">
                Liste de Sophie 🎂
              </h1>
              <p className="text-xs text-muted-foreground">
                Anniversaire dans <span className="font-bold text-primary">{countdown} jours</span>
              </p>
            </div>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" className="rounded-full">
              Retour
            </Button>
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 flex-1">
        <div className="mb-8 text-center">
          <p className="text-2xl md:text-3xl font-normal text-muted-foreground">
            Offre-lui un truc qu'elle veut VRAIMENT pour une fois 😏
          </p>
        </div>

        {/* Priority Filters */}
        <div className="flex flex-wrap gap-2 justify-center mb-8" data-testid="priority-filters">
          <Button
            data-testid="filter-priority-3"
            onClick={() => filterItems(3)}
            variant={activeFilter === 3 ? "default" : "outline"}
            className="rounded-full"
          >
            🔥 J'en rêve même la nuit
          </Button>
          <Button
            data-testid="filter-priority-2"
            onClick={() => filterItems(2)}
            variant={activeFilter === 2 ? "default" : "outline"}
            className="rounded-full"
          >
            😄 Ça me ferait plaisir
          </Button>
          <Button
            data-testid="filter-priority-1"
            onClick={() => filterItems(1)}
            variant={activeFilter === 1 ? "default" : "outline"}
            className="rounded-full"
          >
            😊 Un petit kiff
          </Button>
          {activeFilter && (
            <Button
              data-testid="filter-all"
              onClick={() => { setFilteredItems(items); setActiveFilter(null); }}
              variant="ghost"
              className="rounded-full"
            >
              Tous
            </Button>
          )}
        </div>

        {/* Wishlist Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => {
            const reservedByMe = isReservedByMe(item.id);
            const reservedByOther = isReservedByOther(item);
            const isUnavailable = reservedByMe || reservedByOther;
            
            return (
              <Card 
                key={item.id} 
                className={`shadow-card hover:shadow-float transition-all duration-300 relative overflow-hidden ${
                  isUnavailable ? 'opacity-75' : ''
                }`}
                data-testid={`demo-item-${item.id}`}
              >
                {/* Reserved badges */}
                {reservedByMe && (
                  <div className="absolute top-2 right-2 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold z-10 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Réservé par toi
                  </div>
                )}
                
                {reservedByOther && (
                  <div className="absolute top-2 right-2 bg-muted text-muted-foreground px-3 py-1 rounded-full text-xs font-bold z-10 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Déjà réservé
                  </div>
                )}
                
                {/* Image */}
                <div className="relative h-48 w-full overflow-hidden bg-muted">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {item.priority && PRIORITY_LABELS[item.priority] && (
                    <span className={`absolute bottom-2 right-2 px-3 py-1 rounded-full text-xs font-bold ${PRIORITY_LABELS[item.priority].color}`}>
                      {PRIORITY_LABELS[item.priority].emoji} {PRIORITY_LABELS[item.priority].label}
                    </span>
                  )}
                </div>
                
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <CardTitle className="font-heading text-lg">
                        {item.name}
                      </CardTitle>
                      <p className="text-sm text-primary font-bold mt-1">
                        {item.price}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">{item.category}</p>
                      {item.note && (
                        <p className="text-xs text-muted-foreground mt-2 italic bg-muted/50 px-2 py-1 rounded">
                          "{item.note}"
                        </p>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {renderDemoButton(item, reservedByMe, reservedByOther)}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">
            Tu veux que tes proches puissent faire pareil pour toi ?
          </h2>
          <p className="text-muted-foreground mb-6 text-lg">
            C'est gratuit et ça prend 30 secondes.
          </p>
          <Link to="/register">
            <Button size="lg" className="rounded-full font-bold px-8 py-6 text-lg">
              <Gift className="w-5 h-5 mr-2" />
              Créer ma liste de rêves
            </Button>
          </Link>
        </div>
      </div>

      {/* Reservation Success Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">
              🎉 Super !
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="text-6xl mb-4">🎁</div>
            <p className="text-lg mb-2">
              Tu as réservé <span className="font-bold">{justReservedItem?.name}</span>
            </p>
            <p className="text-muted-foreground">
              Sophie ne le saura pas avant de l'ouvrir 🤫
            </p>
          </div>
          <Button 
            onClick={() => setShowModal(false)} 
            className="w-full rounded-full"
          >
            Continuer à explorer
          </Button>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
